import { useEffect, useState } from "react";
import { api, formatTRY } from "@/lib/apiClient";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { TrendingUp, TrendingDown, Wallet, Percent } from "lucide-react";
import { DateRangeFilter, PRESETS } from "@/components/DateRangeFilter";
import { FinanceDetailDialog } from "@/components/FinanceDetailDialog";

const COLORS = ["var(--chart-1)", "var(--chart-2)", "var(--chart-3)", "var(--chart-4)", "var(--chart-5)"];

const Card = ({ icon: Icon, label, value, color, onClick, testid }) => (
    <div className={`rounded-xl border border-border bg-card p-5 ${onClick ? "cursor-pointer transition-all hover:-translate-y-0.5 hover:border-primary/50" : ""}`} onClick={onClick} role={onClick ? "button" : undefined} data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <Icon className="h-4 w-4" style={{ color }} />
      </div>
      <p className="mt-2 font-heading text-2xl font-extrabold" style={{ color }}>{value}</p>
      {onClick && <p className="mt-1 text-[10px] font-medium uppercase tracking-wider text-primary/80">Detay için tıklayın</p>}
    </div>
  );

export default function Reports() {
  const [d, setD] = useState(null);
  const [range, setRange] = useState(PRESETS.month());
  const [detail, setDetail] = useState(null);
  const [tick, setTick] = useState(0);
  const openDetail = (kind, title) => setDetail({ kind, title, start: range.start, end: range.end });

  useEffect(() => {
    const params = {};
    if (range.start) params.start = range.start;
    if (range.end) params.end = range.end;
    api.get("/dashboard", { params }).then((r) => setD(r.data));
  }, [range, tick]);
  if (!d) return <div className="text-muted-foreground">Yükleniyor…</div>;

  const margin = d.total_income > 0 ? (d.net_profit / d.total_income) * 100 : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Kâr / Zarar Analitiği</h1>
        <p className="mt-1 text-sm text-muted-foreground">İşletmenizin finansal performans raporu</p>
      </div>

      <DateRangeFilter range={range} onChange={setRange} />
      <p className="text-xs text-muted-foreground" data-testid="range-summary">
        Dönem: {range.start || "Başlangıç"} → {range.end || "Bugün"} · {d.total_sales_count} satış
      </p>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Card icon={Wallet} label="Toplam Gelir" value={formatTRY(d.total_income)} color="var(--chart-1)" testid="report-income-card" onClick={() => openDetail("income", "Toplam Gelir Kalemleri")} />
        <Card icon={TrendingDown} label="Giderler + Maliyet" value={formatTRY(d.expenses + d.cogs)} color="var(--chart-3)" testid="report-expense-card" onClick={() => openDetail("expense", "Gider + Maliyet Kalemleri")} />
        <Card icon={d.net_profit >= 0 ? TrendingUp : TrendingDown} label="Net Kâr / Zarar" value={formatTRY(d.net_profit)} color={d.net_profit >= 0 ? "var(--chart-1)" : "var(--destructive)"} />
        <Card icon={Percent} label="Kâr Marjı" value={`%${margin.toFixed(1)}`} color="var(--chart-4)" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-5 lg:col-span-2">
          <h3 className="font-heading text-lg font-semibold">Aylık Gelir / Gider Karşılaştırması</h3>
          <div className="mt-4 h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="month" stroke="var(--muted-foreground)" fontSize={12} />
                <YAxis stroke="var(--muted-foreground)" fontSize={12} />
                <Tooltip contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, color: "var(--foreground)" }} formatter={(v) => formatTRY(v)} />
                <Legend />
                <Bar dataKey="income" name="Gelir" fill="var(--chart-1)" radius={[6, 6, 0, 0]} />
                <Bar dataKey="expense" name="Gider" fill="var(--chart-3)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-5">
          <h3 className="font-heading text-lg font-semibold">Gider Kategorileri</h3>
          {d.expense_by_category.length === 0 ? (
            <p className="mt-10 text-center text-sm text-muted-foreground">Gider kaydı yok</p>
          ) : (
            <div className="mt-2 h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={d.expense_by_category} dataKey="amount" nameKey="category" cx="50%" cy="45%" outerRadius={85} label={(e) => e.category.split(" ")[0]}>
                    {d.expense_by_category.map((e, i) => <Cell key={e.category} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, color: "var(--foreground)" }} formatter={(v) => formatTRY(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="font-heading text-lg font-semibold">Lezzet Satışları</h3>
        <p className="text-xs text-muted-foreground">Lezzet seçenekli ürünlerde (ör. Ice Tea) hangi lezzet ne kadar sattı</p>
        {(!d.flavor_sales || d.flavor_sales.length === 0) ? (
          <p className="mt-8 text-center text-sm text-muted-foreground" data-testid="flavor-sales-empty">Bu dönemde lezzetli ürün satışı yok</p>
        ) : (
          <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-3" data-testid="flavor-sales">
            <div className="h-64 lg:col-span-2">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.flavor_sales} layout="vertical" margin={{ left: 8, right: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                  <XAxis type="number" stroke="var(--muted-foreground)" fontSize={11} allowDecimals={false} />
                  <YAxis type="category" dataKey="flavor" stroke="var(--muted-foreground)" fontSize={12} width={90} />
                  <Tooltip contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, color: "var(--foreground)" }}
                    formatter={(v, n) => (n === "qty" ? [`${v} adet`, "Satış"] : [formatTRY(v), "Ciro"])} />
                  <Bar dataKey="qty" name="qty" radius={[0, 6, 6, 0]}>
                    {d.flavor_sales.map((e, i) => <Cell key={e.flavor} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2">
              {d.flavor_sales.map((f, i) => (
                <div key={f.flavor} className="flex items-center justify-between rounded-lg bg-secondary/40 px-3 py-2 text-sm" data-testid={`flavor-row-${f.flavor}`}>
                  <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{f.flavor}<span className="text-[11px] text-muted-foreground">{f.products.join(", ")}</span></span>
                  <span className="text-right"><span className="font-heading font-bold">{f.qty} adet</span><span className="ml-2 text-xs text-muted-foreground">{formatTRY(f.total)}</span></span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="font-heading text-lg font-semibold">Çeşit Satış Dökümü</h3>
        <p className="text-xs text-muted-foreground">Seçili dönemde çeşitli ürünlerde (ör. Çay 51 gr) her çeşit kaç adet sattı</p>
        {(!d.flavor_by_product || d.flavor_by_product.length === 0) ? (
          <p className="mt-6 text-center text-sm text-muted-foreground" data-testid="flavor-by-product-empty">Bu dönemde çeşitli ürün satışı yok</p>
        ) : (
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="flavor-by-product">
            {d.flavor_by_product.map((p) => (
              <div key={p.product} className="rounded-lg border border-border bg-secondary/30 p-4" data-testid={`flavor-product-${p.product}`}>
                <div className="flex items-center justify-between">
                  <span className="font-heading font-bold">{p.product}</span>
                  <span className="text-xs text-muted-foreground"><b className="text-foreground">{p.qty} adet</b> · {formatTRY(p.total)}</span>
                </div>
                <div className="mt-3 space-y-1.5">
                  {p.flavors.map((f, i) => (
                    <div key={f.flavor} className="text-sm" data-testid={`flavor-product-${p.product}-${f.flavor}`}>
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{f.flavor}</span>
                        <span><b>{f.qty} adet</b><span className="ml-2 text-xs text-muted-foreground">%{p.qty ? ((f.qty / p.qty) * 100).toFixed(0) : 0} · {formatTRY(f.total)}</span></span>
                      </div>
                      <div className="mt-1 h-1 rounded bg-secondary"><div className="h-1 rounded" style={{ width: `${p.qty ? (f.qty / p.qty) * 100 : 0}%`, background: COLORS[i % COLORS.length] }} /></div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="font-heading text-lg font-semibold">Gelir Kırılımı</h3>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-lg bg-secondary/40 p-4">
            <p className="text-xs text-muted-foreground">Ürün Satış Geliri</p>
            <p className="mt-1 font-heading text-xl font-bold">{formatTRY(d.sales_revenue)}</p>
          </div>
          <div className="rounded-lg bg-secondary/40 p-4">
            <p className="text-xs text-muted-foreground">Satılan Ürün Maliyeti</p>
            <p className="mt-1 font-heading text-xl font-bold text-destructive">{formatTRY(d.cogs)}</p>
          </div>
          <div className="rounded-lg bg-secondary/40 p-4">
            <p className="text-xs text-muted-foreground">Brüt Kâr (Satış)</p>
            <p className="mt-1 font-heading text-xl font-bold text-primary">{formatTRY(d.sales_revenue - d.cogs)}</p>
          </div>
        </div>
      </div>
      <FinanceDetailDialog detail={detail} onClose={() => setDetail(null)} onChanged={() => setTick((t) => t + 1)} />
    </div>
  );
}
